import { getIntlString } from '@goparrot-dashboard/i18n';
import classNames from 'classnames';
import React, { FC } from 'react';
import { DroppableProvided, DropResult, ResponderProvided } from 'react-beautiful-dnd';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List, ListRowProps } from 'react-virtualized';
import { ArrowDown, ArrowUp } from '../icons';
import { DragDropProvider, DroppableProvider } from './DragAndDropProvider';
import { GenericTableRow } from './GenericTableRow';
import { Table } from './Table';
import './Table.css';

/**
 * Wrapper that calls CellMeasurer's `measure()` after layout so row
 * heights stay in sync with their content (e.g. when a dropdown expands).
 * Keeps the measurement deferred to the next tick to avoid virtualized
 * render loops while still remeasuring after DOM updates.
 */
export const AutoMeasureRow: FC<{
  measure: () => void;
  registerChild?: (element: HTMLElement | null) => void;
  style: React.CSSProperties;
  className: string;
  children: React.ReactNode;
}> = ({ measure, registerChild, style, className, children }) => {
  const childrenRef = React.useRef<React.ReactNode>();

  React.useLayoutEffect(() => {
    // Measure on first render and when children change
    if (childrenRef.current !== children) {
      childrenRef.current = children;
      // Keep the measurement async to avoid re-entering the virtualized list render cycle.
      const timer = setTimeout(() => {
        measure();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [children, measure]);

  return (
    <div ref={registerChild} style={style} className={className}>
      {children}
    </div>
  );
};

type GenericTablePropsRecord = Record<string, unknown>;

type GenericTableHeaderColumn = {
  className?: string;
  canSort?: boolean;
  sorting?: boolean;
  isSorted?: boolean;
  isSortedDesc?: boolean;
  getHeaderProps: (props?: unknown) => GenericTablePropsRecord;
  getSortByToggleProps?: () => GenericTablePropsRecord;
  render: (slot: 'Header') => React.ReactNode;
};

type GenericTableHeaderGroup = {
  getHeaderGroupProps: () => GenericTablePropsRecord;
  headers: GenericTableHeaderColumn[];
};

type GenericTableRowCell = {
  column: {
    className?: string;
    maxWidth?: number | string;
    id?: string | number;
  };
  getCellProps: () => GenericTablePropsRecord;
  render: (slot: 'Cell') => React.ReactNode;
};

type GenericTableRowData = {
  id: string;
  index: number;
  original: unknown;
  getRowProps: () => GenericTablePropsRecord;
  cells: GenericTableRowCell[];
};

export type GenericTableProps = {
  getTableProps: () => GenericTablePropsRecord;
  getTableBodyProps: () => GenericTablePropsRecord;
  headerGroups: GenericTableHeaderGroup[];
  rows: GenericTableRowData[];
  prepareRow: (row: GenericTableRowData) => void;
  placeholder?: string;
  rowClassName?: string;
  tableClassName?: string;
  fixedHeader?: boolean;
  contentAlign?: 'top' | 'middle';
  onDragEnd?(result: DropResult, provided: ResponderProvided): void;
  isTableVirtualized?: boolean;
  dropdownComponent?: (original: unknown) => React.ReactNode;
  checkRowIsDraggable?(original: unknown): boolean;
  applyClassNameToRow?(row: GenericTableRowData): boolean;
  listRef?: React.RefObject<List>;
  modalLoaded?: boolean; // used when the table is inside a modal to set the height of the table
  hideEmptyPlaceholder?: boolean;
  // Keep this callback referentially stable (module-scope or useCallback) so
  // virtualized tables do not clear their row-measurement cache on every render.
  getRowMeasurementKey?(row: GenericTableRowData, index: number): string;
  // Used as the initial estimate for unmeasured rows in virtualized tables.
  // Values below DEFAULT_ROW_HEIGHT are clamped to the shared minimum row floor.
  virtualizedDefaultRowEstimate?: number;
  virtualizedRowHeight?: number;
  // Keep this callback referentially stable (module-scope or useCallback) so
  // deterministic virtualized tables do not recompute row heights on every render.
  getVirtualizedRowHeight?(row: GenericTableRowData, index: number): number;
};

const getDefaultRowMeasurementKey = (row: GenericTableRowData, index: number): string => {
  if (row?.id) {
    return `row:${row.id}`;
  }

  return `index:${index}`;
};

// TODO add react-table typings, possible solution https://github.com/DefinitelyTyped/DefinitelyTyped/tree/master/types/react-table
export const DEFAULT_ROW_HEIGHT = 75;
export const GenericTable: FC<React.PropsWithChildren<GenericTableProps>> = ({
  getTableProps,
  getTableBodyProps,
  rowClassName = '',
  tableClassName = '',
  headerGroups,
  rows,
  prepareRow,
  placeholder = getIntlString('table.empty.placeholder'),
  fixedHeader,
  onDragEnd,
  contentAlign,
  dropdownComponent,
  isTableVirtualized = false,
  checkRowIsDraggable,
  applyClassNameToRow,
  listRef = null,
  modalLoaded,
  hideEmptyPlaceholder,
  // Keep prop identity (undefined vs provided) for the deterministic
  // row-structure short-circuit below.
  getRowMeasurementKey: getRowMeasurementKeyProp,
  virtualizedDefaultRowEstimate = DEFAULT_ROW_HEIGHT,
  virtualizedRowHeight,
  getVirtualizedRowHeight,
}) => {
  const getRowMeasurementKey = getRowMeasurementKeyProp ?? getDefaultRowMeasurementKey;
  const defaultRowEstimate = Math.max(virtualizedDefaultRowEstimate, DEFAULT_ROW_HEIGHT);
  const hasDeterministicVirtualizedRowHeight = isTableVirtualized && (typeof virtualizedRowHeight === 'number' || !!getVirtualizedRowHeight);
  // Only measurement-based virtualization uses CellMeasurerCache; callers with
  // deterministic heights intentionally skip it.
  const cache = React.useMemo(() => {
    if (!isTableVirtualized || hasDeterministicVirtualizedRowHeight) {
      return null;
    }

    return new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: defaultRowEstimate,
      minHeight: DEFAULT_ROW_HEIGHT,
    });
  }, [defaultRowEstimate, hasDeterministicVirtualizedRowHeight, isTableVirtualized]);
  const internalListRef = React.useRef<List>(null);
  const effectiveListRef = listRef || internalListRef;
  const tableBodyRef = React.useRef<HTMLDivElement | null>(null);
  const observedTableBodyRef = React.useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null);
  const [height, setHeight] = React.useState(0);
  const rowStructureSignature = React.useMemo(() => {
    if (!isTableVirtualized || (hasDeterministicVirtualizedRowHeight && !getRowMeasurementKeyProp)) {
      return '';
    }

    return rows.map((row, index) => getRowMeasurementKey(row, index)).join('|');
  }, [getRowMeasurementKey, getRowMeasurementKeyProp, hasDeterministicVirtualizedRowHeight, isTableVirtualized, rows]);
  let deterministicRowHeightSignature = '';
  if (isTableVirtualized && hasDeterministicVirtualizedRowHeight) {
    // O(n) per render by design: deterministic callbacks can change row height
    // without changing row count, so this effect needs a value-based dependency.
    deterministicRowHeightSignature =
      typeof getVirtualizedRowHeight === 'function'
        ? rows.map((row, index) => getVirtualizedRowHeight(row, index)).join('|')
        : `${virtualizedRowHeight}:${rows.length}`;
  }

  const updateTableBodyHeight = React.useCallback(() => {
    const elHeight = tableBodyRef.current?.clientHeight;
    if (elHeight) {
      setHeight(elHeight);
    }
  }, []);

  // The one-shot measurements below can run while the body still has no layout
  // (e.g. inside a modal that is still opening, or whose web component has not
  // been upgraded yet), which would leave the virtualized list stuck at 0px and
  // render no rows at all. Observing the body keeps the height in sync with
  // whatever layout it ends up with.
  const observeTableBody = React.useCallback(
    (element: HTMLDivElement | null) => {
      if (!element || observedTableBodyRef.current === element || typeof ResizeObserver === 'undefined') {
        return;
      }

      resizeObserverRef.current?.disconnect();
      const resizeObserver = new ResizeObserver(() => {
        updateTableBodyHeight();
      });
      resizeObserver.observe(element);
      resizeObserverRef.current = resizeObserver;
      observedTableBodyRef.current = element;
    },
    [updateTableBodyHeight],
  );

  React.useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      observedTableBodyRef.current = null;
    };
  }, []);

  React.useLayoutEffect(() => {
    updateTableBodyHeight();
  }, [modalLoaded, rowStructureSignature, updateTableBodyHeight]);

  React.useLayoutEffect(() => {
    if (!isTableVirtualized || hasDeterministicVirtualizedRowHeight || !cache) {
      return;
    }

    cache.clearAll();
    effectiveListRef.current?.recomputeRowHeights(0);
  }, [cache, effectiveListRef, hasDeterministicVirtualizedRowHeight, isTableVirtualized, rowStructureSignature]);

  React.useLayoutEffect(() => {
    if (!isTableVirtualized || !hasDeterministicVirtualizedRowHeight) {
      return;
    }

    effectiveListRef.current?.recomputeRowHeights(0);
  }, [deterministicRowHeightSignature, effectiveListRef, hasDeterministicVirtualizedRowHeight, isTableVirtualized]);

  React.useEffect(() => {
    window.addEventListener('resize', updateTableBodyHeight);

    return () => {
      window.removeEventListener('resize', updateTableBodyHeight);
    };
  }, [updateTableBodyHeight]);

  const appliedRowClassName = (row: GenericTableRowData) => {
    if (applyClassNameToRow) {
      return applyClassNameToRow(row) ? rowClassName : '';
    }
    return rowClassName;
  };
  const rowRenderer = ({ index, key, parent, style }: ListRowProps) => {
    const row = rows[index];
    prepareRow(row);

    if (hasDeterministicVirtualizedRowHeight) {
      return (
        <div key={key} style={style} className="tw-table tw-w-full tw-border-collapse">
          <GenericTableRow
            dropdownComponent={dropdownComponent}
            row={row}
            index={index}
            onDragEnd={onDragEnd}
            isTableVirtualized={isTableVirtualized}
            rowClassName={appliedRowClassName(row)}
            contentAlign={contentAlign}
          />
        </div>
      );
    }

    if (!cache) {
      return null;
    }

    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
        {({ measure, registerChild }) => (
          <AutoMeasureRow
            measure={measure}
            registerChild={registerChild as (element: HTMLElement | null) => void}
            style={style}
            className="tw-table tw-w-full tw-border-collapse"
          >
            <GenericTableRow
              dropdownComponent={dropdownComponent}
              row={row}
              index={index}
              onDragEnd={onDragEnd}
              isTableVirtualized={isTableVirtualized}
              rowClassName={appliedRowClassName(row)}
              contentAlign={contentAlign}
            />
          </AutoMeasureRow>
        )}
      </CellMeasurer>
    );
  };
  const listRowHeight = React.useMemo(() => {
    if (getVirtualizedRowHeight) {
      return ({ index }: { index: number }) => getVirtualizedRowHeight(rows[index], index);
    }

    if (typeof virtualizedRowHeight === 'number') {
      return virtualizedRowHeight;
    }

    return cache?.rowHeight ?? DEFAULT_ROW_HEIGHT;
  }, [cache, getVirtualizedRowHeight, rows, virtualizedRowHeight]);

  // eslint-ignore
  return (
    <DragDropProvider onDragEnd={onDragEnd}>
      <Table {...getTableProps()} className={tableClassName} isTableVirtualized={isTableVirtualized}>
        <Table.Head data-testid="table-head" isTableVirtualized={isTableVirtualized}>
          {headerGroups.map((headerGroup) => (
            // eslint-disable-next-line react/jsx-key
            <Table.Row isTableVirtualized={isTableVirtualized} {...headerGroup.getHeaderGroupProps()} className="tw-table-row">
              {headerGroup.headers.map((column) => (
                // eslint-disable-next-line react/jsx-key
                <Table.Heading
                  isTableVirtualized={isTableVirtualized}
                  className={classNames([column.className, fixedHeader ? 'tw-sticky tw-top-0 tw-bg-white tw-z-10' : '', column.canSort ? 'tw-nowrap' : ''])}
                  {...column.getHeaderProps(column.canSort && column.getSortByToggleProps ? column.getSortByToggleProps() : undefined)}
                >
                  {column.render('Header')}
                  {column.sorting && (
                    <span className="tw-ml-1 tw-w-1.5 tw-inline-block">
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <ArrowDown className="tw-h-1.5 tw-w-1.5 tw-text-blue-500" />
                        ) : (
                          <ArrowUp className="tw-h-1.5 tw-w-1.5 tw-text-blue-500" />
                        )
                      ) : (
                        column.sorting && <ArrowDown className="tw-h-1.5 tw-w-1.5" />
                      )}
                    </span>
                  )}
                </Table.Heading>
              ))}
            </Table.Row>
          ))}
        </Table.Head>
        <DroppableProvider isDroppable={!!onDragEnd}>
          {(droppableProvided?: DroppableProvided) => (
            <Table.Body
              isTableVirtualized={isTableVirtualized}
              {...getTableBodyProps()}
              innerRef={
                ((element: HTMLDivElement | HTMLTableSectionElement | null) => {
                  if (isTableVirtualized) {
                    tableBodyRef.current = element as HTMLDivElement | null;
                    observeTableBody(element as HTMLDivElement | null);
                  }

                  if (droppableProvided?.innerRef) {
                    droppableProvided.innerRef(element);
                  }
                }) as (...args: unknown[]) => unknown
              }
            >
              {rows.length ? (
                isTableVirtualized ? (
                  <div style={{ height: `${height}px` }} className={classNames(['tw-w-full'])}>
                    <AutoSizer className="tw-w-full tw-h-full">
                      {({ height, width }) => (
                        <List
                          ref={effectiveListRef}
                          style={{ paddingRight: '0' }}
                          deferredMeasurementCache={cache ?? undefined}
                          rowHeight={listRowHeight}
                          width={width}
                          height={height}
                          rowRenderer={rowRenderer}
                          rowCount={rows.length}
                          overscanRowCount={10}
                        />
                      )}
                    </AutoSizer>
                  </div>
                ) : (
                  rows.map((row, index) => {
                    prepareRow(row);
                    return (
                      <React.Fragment key={`row-${index}`}>
                        <GenericTableRow
                          row={row}
                          index={index}
                          onDragEnd={onDragEnd}
                          isDragDisabled={checkRowIsDraggable}
                          isTableVirtualized={isTableVirtualized}
                          rowClassName={appliedRowClassName(row)}
                          contentAlign={contentAlign}
                          dropdownComponent={dropdownComponent}
                        />
                      </React.Fragment>
                    );
                  })
                )
              ) : (
                !hideEmptyPlaceholder && (
                  <Table.Row isTableVirtualized={isTableVirtualized}>
                    <Table.Data isTableVirtualized={isTableVirtualized}>
                      <div className="tw-py-1.5 tw-px-1 tw-font-normal tw-text-sm tw-text-blue-gray-400">{placeholder}</div>
                    </Table.Data>
                    {Array(headerGroups[0]?.headers?.length - 1)
                      .fill(1)
                      .map((_, index) => (
                        <Table.Data isTableVirtualized={isTableVirtualized} key={index} />
                      ))}
                  </Table.Row>
                )
              )}
              {droppableProvided?.placeholder}
            </Table.Body>
          )}
        </DroppableProvider>
      </Table>
    </DragDropProvider>
  );
};
